import React from 'react'
import PropTypes from 'prop-types'
import { cn } from 'shared/util/cn'

export function Card({ children, className = '', onClick }) {
  return (
    <div onClick={onClick} className={cn('card-surface p-4', onClick && 'cursor-pointer', className)}>
      {children}
    </div>
  )
}

Card.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  onClick: PropTypes.func
}

export function Badge({ children, tone = 'neutral', className = '' }) {
  const tones = {
    neutral: 'bg-dark-800 text-dark-300',
    success: 'bg-[#E2F4EE] text-[#008F61]',
    warning: 'bg-primary-900 text-primary-200',
    danger: 'bg-theme-900 text-theme',
    info: 'bg-secondary-900 text-secondary-400'
  }
  return (
    <span className={cn('inline-flex items-center rounded-full px-2 py-1 text-xxs font-semibold uppercase tracking-wide', tones[tone], className)}>
      {children}
    </span>
  )
}

Badge.propTypes = {
  children: PropTypes.node,
  tone: PropTypes.string,
  className: PropTypes.string
}

export function StatusPill({ status }) {
  if (status === 'in' || status === 'a') return <Badge tone="success">In</Badge>
  if (status === 'partial') return <Badge tone="warning">Partially in</Badge>
  if (status === 'out') return <Badge tone="danger">Not in</Badge>
  if (status === 'active') return <Badge tone="success">Active</Badge>
  if (status === 'inactive') return <Badge tone="neutral">Inactive</Badge>
  return <Badge>{status}</Badge>
}

StatusPill.propTypes = {
  status: PropTypes.string
}

export function EmptyState({ title, message, icon, action }) {
  return (
    <div className="flex min-h-[240px] flex-col items-center justify-center px-6 text-center">
      <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-elevated text-3xl">{icon || '🎟️'}</div>
      <h3 className="mb-2 text-2xl font-semibold text-ink">{title}</h3>
      <p className="mb-0 max-w-sm text-muted">{message}</p>
      {action}
    </div>
  )
}

EmptyState.propTypes = {
  title: PropTypes.string,
  message: PropTypes.string,
  icon: PropTypes.node,
  action: PropTypes.node
}

export function PermissionEmpty() {
  return (
    <EmptyState
      icon="🔒"
      title="You don't have access"
      message="You don't have access to this feature — ask your organiser to enable it."
    />
  )
}

export function Skeleton({ className = '' }) {
  return <div className={cn('animate-pulse rounded-2xl bg-line/70', className)} />
}

Skeleton.propTypes = {
  className: PropTypes.string
}

export function Segmented({ options, value, onChange, className = '' }) {
  return (
    <div className={cn('flex rounded-2xl bg-elevated p-1', className)}>
      {options.map((option) => {
        const id = option.value ?? option
        const label = option.label ?? option
        const active = value === id
        return (
          <button
            key={id}
            type="button"
            onClick={() => onChange(id)}
            className={cn(
              'min-h-tap flex-1 rounded-xl px-3 text-sm font-semibold',
              active ? 'bg-theme text-light shadow-glow' : 'text-muted'
            )}
          >
            {label}
          </button>
        )
      })}
    </div>
  )
}

Segmented.propTypes = {
  options: PropTypes.array.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  className: PropTypes.string
}

export function Stepper({ value, min = 1, max = 10, onChange }) {
  return (
    <div className="flex items-center gap-3">
      <button
        type="button"
        className="flex h-12 w-12 items-center justify-center rounded-2xl bg-elevated text-2xl text-ink disabled:cursor-not-allowed disabled:opacity-40"
        onClick={() => onChange(Math.max(min, value - 1))}
        disabled={value <= min}
        aria-label="Decrease guest count"
      >
        −
      </button>
      <span className="min-w-10 text-center text-5xl font-bold text-ink">{value}</span>
      <button
        type="button"
        className="flex h-12 w-12 items-center justify-center rounded-2xl bg-theme text-2xl text-light disabled:cursor-not-allowed disabled:bg-dark-600 disabled:opacity-40"
        onClick={() => onChange(Math.min(max, value + 1))}
        disabled={value >= max}
        aria-label="Increase guest count"
      >
        +
      </button>
    </div>
  )
}

Stepper.propTypes = {
  value: PropTypes.number.isRequired,
  min: PropTypes.number,
  max: PropTypes.number,
  onChange: PropTypes.func.isRequired
}
